import * as React from 'react';
import { Options } from '@common/components';
import { Control, Errors, actions, Form } from 'react-redux-form';

import { OptionsViewModel } from '@models/common.models';

import { hasValue, getDefaultErrorMessages, isDecimal, valueIsLessThanN } from '@common/validators';

import { ProductGroupViewModel, PriceAdjustmentViewModel, EmployeeDiscountViewModel } from '@models/Product';
import { Dispatch } from 'react-redux';
import { ContractViewModel, ContractOrderType } from '@models/Contract';

import * as classes from '../coverage.scss';
import FormNames from '@constants/FormNames.enum';
import { EditPermissions, EditableFields } from '@models/EditPermissions';
import { parseCurrency } from '@common/parsers';

export interface PriceAdjustmentsComponentPropertiesAttributes {
  readonly productGroups: Array<ProductGroupViewModel>;
  readonly products: Array<OptionsViewModel>;
  readonly priceAdjustments: Array<PriceAdjustmentViewModel>;
  readonly hasSellersCoverage: boolean;
  readonly isQuote: boolean;

  readonly propertyTypeId: number; // from ownProps
  readonly stateAbbreviation: string; // from ownProps
  readonly employeeDiscounts: Array<EmployeeDiscountViewModel>; // employee discounts from ownProps

  readonly selectedProductId?: number;
  readonly selectedGroupId?: number;
  readonly selectedPriceAdjustment?: PriceAdjustmentViewModel;
  readonly isEmployeeDiscountEnabled: boolean;
  readonly buttonRef: (button: HTMLButtonElement) => void;
  readonly selectedEmployeeDiscount?: EmployeeDiscountViewModel;
  readonly overridePriceAdjustmentAmount?: number;
  readonly orderType?: ContractOrderType;
  readonly contractVM: ContractViewModel;
  readonly isOrderEdit?: boolean;
  readonly isOpenContract?: boolean;
}

export interface PriceAdjustmentsComponentProperties extends PriceAdjustmentsComponentPropertiesAttributes {
  readonly selectPriceAdjustment: (adjustment: PriceAdjustmentViewModel) => void;
  readonly setPriceAdjustmentAmount: (amount: number) => void;
  readonly onSaveClick: (model: PriceAdjustmentForm) => void;
  readonly selectEmployeeDiscount: (employeeDiscount: EmployeeDiscountViewModel) => void;
}

interface PriceAdjustmentForm {
  priceAdjustmentAmount: number;
  priceAdjustmentNotes: string;
  employeeDiscount: OptionsViewModel;
}

export class PriceAdjustmentsComponent extends React.Component<PriceAdjustmentsComponentProperties> {
  formDispatch: Dispatch<PriceAdjustmentForm>;

  constructor(props: PriceAdjustmentsComponentProperties) {
    super(props);
  }

  componentDidMount() {
    this.formDispatch(
      actions.change(
        FormNames.PriceAdjustmentForm + '.priceAdjustment.id',
        this.props.selectedPriceAdjustment === undefined ? 0 : this.props.selectedPriceAdjustment.id
      )
    );
    this.formDispatch(
      actions.change(
        FormNames.PriceAdjustmentForm + '.priceAdjustmentAmount',
        this.props.selectedEmployeeDiscount === undefined
          ? this.props.overridePriceAdjustmentAmount === undefined
            ? this.props.selectedPriceAdjustment === undefined
              ? 0
              : this.props.selectedPriceAdjustment.adjustmentAmount
            : this.props.overridePriceAdjustmentAmount
          : this.props.selectedPriceAdjustment === undefined
            ? 0
            : this.props.selectedPriceAdjustment.adjustmentAmount
      )
    );
    this.formDispatch(
      actions.change(
        FormNames.PriceAdjustmentForm + '.priceAdjustmentNotes',
        this.props.contractVM === undefined
          ? ''
          : this.props.contractVM!.priceAdjustmentNotes === undefined
            ? ''
            : this.props.contractVM!.priceAdjustmentNotes
      )
    );
  }

  renderOptions(options: Array<OptionsViewModel>) {
    return options.map((option: OptionsViewModel, index: number) => {
      return (
        <option key={option.id + option.value} value={option.id}>
          {option.value}
        </option>
      );
    });
  }

  canSetPriceAdjustment(): boolean {
    return (
      this.props.selectedPriceAdjustment === undefined || this.props.selectedPriceAdjustment.adjustmentAmount !== 0
    );
    // return (this.props.contractVM && this.props.contractVM.priceAdjustment && (this.props.contractVM.priceAdjustment.adjustmentAmount > 0)) || (this.props.priceAdjustmentId <= 0);
  }

  handleSelectPriceAdjustment = (event: React.SyntheticEvent<HTMLSelectElement>): void => {
    if (!(event.currentTarget || {}).value) {
      return;
    }
    const adjustmentId = +event.currentTarget.value;
    this.setSelectPriceAdjustment(adjustmentId);
  };

  setSelectPriceAdjustment = (value: number): void => {
    const adjustmentId = value;
    const adjustment = this.props.priceAdjustments.find(x => x.id === adjustmentId);

    if (!adjustment && adjustmentId >= 0) {
      return;
    }
    this.props.selectPriceAdjustment(adjustment || ({ id: adjustmentId } as PriceAdjustmentViewModel));
    let adjustmentAmount = (adjustment || ({} as PriceAdjustmentViewModel)).adjustmentAmount || 0;
    this.formDispatch(actions.change(FormNames.PriceAdjustmentForm + '.priceAdjustmentAmount', adjustmentAmount));
  };

  handleSelectEmployeeDiscount = (event: React.SyntheticEvent<HTMLSelectElement>): void => {
    if (!(event.currentTarget || {}).value) {
      return;
    }

    const id = +event.currentTarget.value;

    const discount = this.props.employeeDiscounts.find(x => x.id === id);
    if (!discount && id >= 0) {
      return;
    }
    this.props.selectEmployeeDiscount(discount || ({ id: id } as EmployeeDiscountViewModel));
    return;
  };

  attachDispatch(dispatch: Dispatch<PriceAdjustmentForm>) {
    this.formDispatch = dispatch;
  }

  updatePriceAdjustmentAmount = (event: React.SyntheticEvent<HTMLInputElement>) => {
    this.props.setPriceAdjustmentAmount(+event.currentTarget.value);
  };

  getFormValidators() {
    return {
      'priceAdjustment.id': {
        selection: (val: any) =>
          this.props.selectedPriceAdjustment !== undefined && this.props.selectedPriceAdjustment.id !== -1
      }
    };
  }

  render() {
    return (
      <Form
        model={FormNames.PriceAdjustmentForm}
        getDispatch={(dispatch: Dispatch<PriceAdjustmentForm>) => this.attachDispatch(dispatch)}
        validateOn="submit"
        validators={this.getFormValidators()}
        onSubmit={this.props.onSaveClick}
        hideNativeErrors={true} /*onSubmit={this.props.handleSave}*/
      >
        <div className="row">
          <div className="col-md-12">
            <h4>Price Adjustment</h4>
          </div>
        </div>
        <div className="row">
          <div className={`row mb-10 ${classes.priceAdjustment}`}>
            <div className="col-md-8">
              <label htmlFor={FormNames.PriceAdjustmentForm + '.priceAdjustment'}>Price Adj.</label>
              <Control.select
                onChange={this.handleSelectPriceAdjustment}
                value={this.props.selectedPriceAdjustment ? this.props.selectedPriceAdjustment.id : -1}
                model=".priceAdjustment.id"
                validators={{ selection: hasValue }}
                disabled={
                  this.props.isOrderEdit &&
                  !EditPermissions.CanEditField(EditableFields.PriceAdjustment, this.props.isOpenContract)
                }
              >
                <Options options={this.props.priceAdjustments} />
              </Control.select>
              <Errors
                className="error"
                model=".priceAdjustment.id"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
            <div className="col-md-4">
              <label htmlFor={FormNames.PriceAdjustmentForm + '.priceAdjustmentAmount'}>Amount</label>
              <Control.text
                // value must be loaded in on mount
                //  value={this.props.overridePriceAdjustmentAmount !== undefined ? this.props.overridePriceAdjustmentAmount : (this.props.selectedPriceAdjustment || {} as PriceAdjustmentViewModel).adjustmentAmount || 0}
                model=".priceAdjustmentAmount"
                onKeyUp={event => {
                  this.updatePriceAdjustmentAmount(event);
                }}
                disabled={
                  this.canSetPriceAdjustment() ||
                  this.props.selectedPriceAdjustment === undefined ||
                  this.props.selectedPriceAdjustment.id === 0 ||
                  (this.props.isOrderEdit &&
                    !EditPermissions.CanEditField(EditableFields.PriceAdjustment, this.props.isOpenContract))
                }
                min="0.00"
                step="0.01"
                type="number"
                validators={{ isDecimal, lessThan10000: valueIsLessThanN(10000) }}
                parser={parseCurrency}
              />
              <Errors
                className="error"
                model=".priceAdjustmentAmount"
                show={{ touched: true, focus: false }}
                messages={getDefaultErrorMessages()}
              />
            </div>
          </div>
          {this.props.employeeDiscounts.length > 0 && (
            <div className={`row mb-10 ${classes.priceAdjustment}`}>
              <div className="col-md-12 ">
                <label htmlFor={FormNames.PriceAdjustmentForm + '.employeeDiscount'}>Employee Discount</label>
                <Control.select
                  value={(this.props.selectedEmployeeDiscount || ({ id: -1 } as { id: number })).id}
                  model=".employeeDiscount.id"
                  disabled={
                    !this.props.isEmployeeDiscountEnabled ||
                    (this.props.isOrderEdit &&
                      !EditPermissions.CanEditField(EditableFields.PriceAdjustment, this.props.isOpenContract))
                  }
                  onChange={this.handleSelectEmployeeDiscount}
                >
                  <Options options={this.props.employeeDiscounts} />
                </Control.select>
              </div>
            </div>
          )}
          {this.props.isQuote ? (
            this.props.selectedPriceAdjustment === undefined || this.props.selectedPriceAdjustment.id === 0 ? (
              ''
            ) : (
              <div className={`row mb-10 ${classes.priceAdjustment}`}>
                <div className="col-md-12">
                  <label htmlFor={FormNames.PriceAdjustmentForm + '.priceAdjustmentNotes'}>
                    Reason for price adjustment
                  </label>
                  <Control.text
                    model=".priceAdjustmentNotes"
                    validators={{ required: hasValue }}
                    disabled={
                      this.props.isOrderEdit &&
                      !EditPermissions.CanEditField(EditableFields.PriceAdjustment, this.props.isOpenContract)
                    }
                  />
                  <Errors
                    className="error"
                    model=".priceAdjustmentNotes"
                    show={{ touched: true, focus: false }}
                    messages={getDefaultErrorMessages()}
                  />
                </div>
              </div>
            )
          ) : (
            ''
          )}
        </div>

        {/* ends here */}
        <div className="col-md-1">
          <button className="btn btn-hidden" type="submit" ref={this.props.buttonRef}>
            Save
          </button>
        </div>
      </Form>
    );
  }
}



// WEBPACK FOOTER //
// ./src/contract/_components/coverage/_components/price-adjustments.component.tsx